/**
 * Identity avatars: the tint behind a face illustration or a set of initials,
 * and the generated face itself.
 *
 * Both are derived from an entity's id rather than stored, so the same persona
 * draws the same circle everywhere it appears — the personas table, the picker,
 * and every transcript that quotes it.
 *
 * This lives in `shared` because the transcript components are shared and the
 * persona module is not the only caller any more. The tints themselves are
 * design-system tokens (`--coval-avatar-N`, in `@coval/ui`'s avatar-tokens.css);
 * only the selection rule lives here.
 */

// Brand stops, not the data-viz palette: an avatar tint is chrome carrying
// identity, so nothing reads a quantity off it. Resolved through the tokens so
// the literals stay in the token layer — every consumer applies them as an
// inline backgroundColor, which takes a var() reference.
export const AVATAR_COLORS = [
  "var(--coval-avatar-1)", // blue 100
  "var(--coval-avatar-2)", // blue 200
  "var(--coval-avatar-3)", // green 100
  "var(--coval-avatar-4)", // orange 100
  "var(--coval-avatar-5)", // orange 200
  "var(--coval-avatar-6)", // purple 100
  "var(--coval-avatar-7)", // purple 200
  "var(--coval-avatar-8)", // warm neutral 200
]

// Each tint's family dark stop, index-aligned with AVATAR_COLORS.
const AVATAR_INKS = AVATAR_COLORS.map(
  (_, index) => `var(--coval-avatar-${index + 1}-ink)`,
)

const hashAvatarIndex = (id: string): number => {
  let hash = 0
  for (let index = 0; index < id.length; index++) {
    hash = id.charCodeAt(index) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % AVATAR_COLORS.length
}

/**
 * The initials color for an entity: the dark stop of the same family as
 * `getAvatarColor(id)`'s tint.
 */
export const getAvatarInk = (id: string): string =>
  AVATAR_INKS[hashAvatarIndex(id)]

/**
 * The avatar backdrop tint for an entity, derived from its id so the same one
 * always gets the same tint without storing one. With no id — a persona being
 * created — falls back to `fallbackIndex` when the caller holds a stable index,
 * and otherwise picks at random.
 */
export const getAvatarColor = (id: string, fallbackIndex?: number): string => {
  if (!id) {
    return fallbackIndex === undefined
      ? AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]
      : AVATAR_COLORS[fallbackIndex % AVATAR_COLORS.length]
  }

  return AVATAR_COLORS[hashAvatarIndex(id)]
}

// Initials avatars pair a brand family's light stop with its darkest stop, so
// the letters read in the same hue as the circle behind them.
const INITIALS_AVATAR_FAMILIES = ["blue", "green", "orange", "purple"] as const

/** Background and ink for an initials avatar, derived from the entity's id. */
export const getInitialsAvatarColors = (
  id: string,
): { background: string; ink: string } => {
  let hash = 0
  for (let index = 0; index < id.length; index++) {
    hash = id.charCodeAt(index) + ((hash << 5) - hash)
  }
  const family =
    INITIALS_AVATAR_FAMILIES[(hash >>> 0) % INITIALS_AVATAR_FAMILIES.length]
  return {
    background: `var(--coval-${family}-100)`,
    ink: `var(--coval-${family}-900)`,
  }
}

export interface FaceCustomization {
  hair: string
  eyes: string
  lips: string
}

/** The generated face for a seed, optionally with chosen features. */
export const getAvatarUrl = (
  seed?: string,
  options?: Partial<FaceCustomization>,
) => {
  const avatarSeed = seed || Math.random().toString(36).substring(7)
  const params = new URLSearchParams({ seed: avatarSeed })

  if (options?.hair) {
    params.append("hair", options.hair)
  }

  if (options?.eyes) {
    params.append("eyes", options.eyes)
  }

  if (options?.lips) {
    params.append("lips", options.lips)
  }

  return `https://api.dicebear.com/9.x/notionists/svg?${params.toString()}`
}
